import { Suspense } from "react";
import type { AreaSymbol } from "@/lib/layouts/types";
import type { UnpublishedWindow, WindowsLayoutState } from "@/lib/windows/types";
import { WindowRenderer } from "@/components/windows/WindowRenderer/WindowRenderer";
import { WindowSkeleton } from "@/components/skeletons/WindowSkeleton/WindowSkeleton";
import styles from "./WindowAreaView.module.css";

/**
 * WindowAreaView — widok obszaru z oknami renderowany IDENTYCZNIE dla admina
 * i gościa (po unifikacji visit page).
 *
 * **Renderowanie okien:** dla każdego windowId z `publishedByArea[name]`:
 *   1. Lookup metadata w `windowsById` (Map zbudowana w WindowArea wrapper)
 *   2. Wrapper `<article>` z `data-*` attributes (debugowanie + ewentualny
 *      styling per typ komponentu)
 *   3. `<WindowRenderer>` renderuje content na podstawie `componentSymbol`
 *
 * **Bez DnD overlays** — admin edytuje przez panel `LayoutAndWindowsPanel`
 * (mini-mapa układu pod listą nieopublikowanych okien). Strona renderuje się
 * zawsze jako pure visitor view.
 */
export function WindowAreaView({ name, windowsState, windowsById }: WindowAreaViewProps) {
  /* `name` to `string` (prop) — runtime'owo zawsze `AreaSymbol`, cast eliminuje
     TS index signature mismatch (`Partial<Record<AreaSymbol, ...>>`). */
  const windowIds: number[] = windowsState.publishedByArea[name as AreaSymbol] ?? [];

  if (windowIds.length === 0) {
    return <div className={styles.empty} data-area-name={name} aria-hidden="true" />;
  }

  return (
    <div className={styles.root} data-area-name={name}>
      {windowIds.map((id) => {
        const meta = windowsById.get(id);
        if (!meta) {
          /* Defensive: window został usunięty z `unpublishedWindows` (np.
             admin usunął instancję z DB), ale state lokalny jeszcze go ma.
             Renderujemy placeholder żeby nie crashować. */
          return (
            <article
              key={id}
              className={styles.windowMissing}
              data-window-id={id}
              aria-label={`Brak metadanych dla okna #${id}`}
            />
          );
        }
        /* Background z `imp_cms_window.config[background]` jest aplikowany
           wewnątrz kontenera okna (`WDefault`/`WEmpty`) wybranego przez
           dispatch lambda w `variants.tsx`. WindowAreaView NIE aplikuje
           background tutaj — outer `<article>` to tylko stable slot ID. */
        return (
          <article
            key={id}
            className={styles.window}
            data-window-id={meta.id}
            data-window-symbol={meta.componentSymbol}
          >
            {/* Per-window Suspense boundary: gdy `WindowRenderer.use(promise)`
                suspend'uje (czeka na fetch'owanie contentu), React pokazuje
                `<WindowSkeleton />` w międzyczasie. Po resolve'ie promise'a
                stream zamienia na real content — niezależnie od innych okien
                (każde okno ma własny Suspense + własne fetchowanie).
                Page-level fallback (przed SSR completion) — brak; `loading.tsx`
                zwraca null, browser zostaje na poprzedniej stronie. */}
            <Suspense fallback={<WindowSkeleton />}>
              <WindowRenderer window={meta} area={name as AreaSymbol} />
            </Suspense>
          </article>
        );
      })}
    </div>
  );
}

export type WindowAreaViewProps = {
  /** Symbol obszaru (np. "main", "headerCol1"). Używany jako data-area-name. */
  name: string;
  /** Snapshot windows state — read-only z perspektywy view component'u. */
  windowsState: WindowsLayoutState;
  /** O(1) lookup map z metadata wszystkich okien (zbudowana w WindowArea
      wrapper przez useMemo). Klucz = `imp_cms_window.id`. */
  windowsById: Map<number, UnpublishedWindow>;
};
